import { useState } from 'react';
import { ComponentShowcase } from './ComponentShowcase';
import { StatusBadge } from '@/ui/StatusBadge';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { StatCard } from '@/ui/StatCard';
import { TegLogo } from '@/ui/TegLogo';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DataPagination } from '@/ui/DataPagination';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import { Button } from '@/ui/base/button';
import { Users, Car, FileText, DollarSign, Search, Trash2, AlertTriangle } from 'lucide-react';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { ErrorState } from '@/ui/ErrorState';

import { AmostrasDeDetalhe } from './amostras/Detalhe';
import { AmostrasDeListagem } from './amostras/Listagens';
import { AmostrasDeValores } from './amostras/Valores';
import { CPF_MASCARADO, CREDENCIADOS_DE_EXEMPLO, type CredenciadoDeExemplo } from './exemplos';

const COLUNAS_DE_EXEMPLO: DataTableColumn<CredenciadoDeExemplo>[] = [
  { header: 'Nome', accessorKey: 'nome' },
  { header: 'CPF', accessorKey: 'cpf' },
  { header: 'DRE', accessorKey: 'dre' },
  { header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
];

const COLUNAS_DA_VISAO_DE_EXEMPLO: TableViewColumn<CredenciadoDeExemplo>[] = [
  { id: 'nome', header: 'Nome', accessorKey: 'nome' },
  { id: 'cpf', header: 'CPF', accessorKey: 'cpf' },
  { id: 'dre', header: 'DRE', accessorKey: 'dre', linkedFilterKeys: ['dre'] },
  {
    id: 'status',
    header: 'Status',
    isStatusColumn: true,
    canHide: false,
    cell: (row) => <StatusBadge status={row.status} />,
  },
];

export function ComponentesSistemaTab() {
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    busca: '',
    status: '',
  });
  const [paginationPage, setPaginationPage] = useState(1);
  const [paginationPerPage, setPaginationPerPage] = useState(10);

  // Filtro por "dre" fixo, só pra demonstrar o ponto de alerta no ícone quando
  // a coluna que explica um filtro em vigor está oculta (ver comentário de
  // TableViewControls.tsx).
  const demoTableView = useTableViewPreferences({
    contextKey: 'design-system:demo',
    columns: COLUNAS_DA_VISAO_DE_EXEMPLO,
    filterValues: { dre: 'Campo Limpo' },
  });

  const filterFields: FilterFieldConfig[] = [
    { key: 'busca', label: 'Busca', type: 'search', placeholder: 'Nome ou CPF...' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ativo', label: 'Ativo' },
        { value: 'pendente', label: 'Pendente' },
        { value: 'bloqueado', label: 'Bloqueado' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <h3 id="sistema-listagens" className="pt-2 text-lg font-semibold">
        Listagens
      </h3>
      {/* DataTable */}
      <ComponentShowcase
        id="data-table"
        title="DataTable"
        description="Tabela padronizada: cartão, esqueleto enquanto carrega, estado vazio e paginação. Coluna por accessorKey ou por função cell."
        code={`<DataTable
  columns={[
    { header: "Nome", accessorKey: "nome" },
    { header: "Status", cell: (row) => <StatusBadge status={row.status} /> },
  ]}
  data={credenciados}
  isLoading={isLoading}
  page={page}
  perPage={perPage}
  total={total}
  onPageChange={setPage}
  onPerPageChange={setPerPage}
  onRowClick={(row) => navigate(\`/credenciados/\${row.id}\`)}
  emptyTitle="Nenhum credenciado encontrado"
  emptyIcon={Search}
/>`}
        propsTable={[
          [
            'columns',
            'DataTableColumn<T>[]',
            '-',
            'Definição das colunas (header, accessorKey, cell)',
          ],
          ['data', 'T[]', '-', 'Linhas (T extends { id: string })'],
          ['isLoading', 'boolean', 'false', 'Mostra o esqueleto da tabela'],
          ['page', 'number', '-', 'Página atual'],
          ['perPage', 'number', '-', 'Itens por página'],
          ['total', 'number', '-', 'Total de registros para paginação'],
          ['onPageChange', '(page) => void', '-', 'Chamado ao trocar de página'],
          ['onPerPageChange', '(n) => void', '-', 'Chamado ao trocar a quantidade por página'],
          ['onRowClick', '(row) => void', '-', 'Torna a linha inteira clicável (abre o detalhe)'],
          ['emptyIcon', 'LucideIcon', 'Inbox', 'Ícone do EmptyState'],
          ['emptyTitle', 'string', '-', 'Título quando não há linhas'],
        ]}
      >
        <div className="w-full">
          <DataTable
            columns={COLUNAS_DE_EXEMPLO}
            data={CREDENCIADOS_DE_EXEMPLO}
            ariaLabel="Tabela de credenciados (exemplo)"
          />
        </div>
      </ComponentShowcase>
      {/* FilterBar */}
      <ComponentShowcase
        id="filter-bar"
        title="FilterBar"
        description="Barra de filtros com campos de busca, seleção, data e mês. 'Limpar filtros' só aparece quando há filtro ativo."
        code={`<FilterBar
  fields={[
    { key: "busca", label: "Busca", type: "search", placeholder: "Nome ou CPF..." },
    { key: "status", label: "Status", type: "select", options: [...] },
    { key: "mes", label: "Mês", type: "month" },
  ]}
  values={filterValues}
  onChange={(key, value) => setFilterValues(prev => ({ ...prev, [key]: value }))}
  onClear={() => setFilterValues({})}
/>`}
        propsTable={[
          [
            'fields',
            'FilterFieldConfig[]',
            '-',
            'Configuração dos campos (key, label, type, options)',
          ],
          ['values', 'Record<string, string>', '-', 'Valores atuais dos filtros'],
          ['onChange', '(key, value) => void', '-', 'Chamado ao mudar um filtro'],
          ['onClear', '() => void', '-', 'Chamado por Limpar filtros'],
          ['hasActiveFilters', 'boolean', 'auto', 'Força a visibilidade de Limpar filtros'],
        ]}
      >
        <div className="w-full">
          <FilterBar
            fields={filterFields}
            values={filterValues}
            onChange={(key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }))}
            onClear={() => setFilterValues({ busca: '', status: '' })}
          />
        </div>
      </ComponentShowcase>
      <ComponentShowcase
        id="personalizar-colunas"
        title="Personalizar colunas"
        description="Padrão único para personalização de colunas de uma listagem: mostrar/ocultar/reordenar, guardado em localStorage por usuário. O botão fica sempre na ponta direita do cabeçalho da DataTable (visível mesmo com rolagem lateral) e só se liga pela prop personalizacao — nenhuma tela o põe numa barra acima da tabela. Sem visão salva/compartilhada nem auditoria — decisão do dono do produto em 2026-08-21 (ver comentário de useTableViewPreferences.ts)."
        code={`const tableView = useTableViewPreferences({
  contextKey: "credenciados:list",
  columns,
  filterValues,
});

<DataTable
  columns={tableView.orderedColumns}
  personalizacao={tableView}
  data={rows}
/>`}
        propsTable={[
          [
            'personalizacao',
            'PreferenciasDaTabela<T>',
            '-',
            'Prop da DataTable: retorno de useTableViewPreferences({ contextKey, columns, filterValues }). Liga o botão na ponta direita do cabeçalho',
          ],
          [
            'columns',
            'TableViewColumn<T>[]',
            '-',
            'Sempre personalizacao.orderedColumns, para a tabela seguir a escolha do usuário',
          ],
        ]}
      >
        <div className="w-full space-y-3">
          <DataTable
            columns={demoTableView.orderedColumns}
            personalizacao={demoTableView}
            data={CREDENCIADOS_DE_EXEMPLO}
            ariaLabel="Tabela de credenciados com colunas personalizáveis (exemplo)"
          />

          <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            Exemplo com filtro fixo em "dre": ao ocultar a coluna DRE, o ícone ganha um ponto de
            alerta e o modal avisa que um filtro em vigor usa uma coluna oculta.
          </div>
        </div>
      </ComponentShowcase>
      {/* DataPagination */}
      <ComponentShowcase
        id="data-pagination"
        title="DataPagination"
        description="Paginação com seletor de itens por página, 'X–Y de Z registros', números com reticências e botões de primeira e última página."
        code={`<DataPagination
  page={page}
  perPage={perPage}
  total={127}
  onPageChange={setPage}
  onPerPageChange={setPerPage}
/>`}
        propsTable={[
          ['page', 'number', '-', 'Página atual'],
          ['perPage', 'number', '-', 'Itens por página'],
          ['total', 'number', '-', 'Total de registros'],
          ['onPageChange', '(page) => void', '-', 'Chamado ao trocar de página'],
          ['onPerPageChange', '(n) => void', '-', 'Chamado ao trocar a quantidade por página'],
        ]}
      >
        <div className="w-full rounded-xl border border-border bg-card">
          <DataPagination
            page={paginationPage}
            perPage={paginationPerPage}
            total={127}
            onPageChange={setPaginationPage}
            onPerPageChange={setPaginationPerPage}
          />
        </div>
      </ComponentShowcase>
      <AmostrasDeListagem />

      <h3 id="sistema-valores" className="pt-2 text-lg font-semibold">
        Valores e selos
      </h3>
      {/* StatusBadge */}
      <ComponentShowcase
        id="status-badge"
        title="StatusBadge"
        description="Selo de situação — cor pelos tokens success, warning, destructive e info"
        code={`<StatusBadge status="ativo" />
<StatusBadge status="pendente" />
<StatusBadge status="bloqueado" />
<StatusBadge status="processado" />
<StatusBadge status="cancelado" label="Cancelado" />`}
        propsTable={[
          ['status', 'StatusVariant', '-', 'Situação: ativo, pendente, bloqueado etc.'],
          ['label', 'string', 'status', 'Texto exibido (padrão: nome da situação)'],
        ]}
      >
        <div className="flex flex-wrap gap-2">
          <StatusBadge status="ativo" />
          <StatusBadge status="pendente" />
          <StatusBadge status="suspenso" />
          <StatusBadge status="bloqueado" />
          <StatusBadge status="processado" />
          <StatusBadge status="cancelado" />
          <StatusBadge status="aprovado" />
          <StatusBadge status="rejeitado" />
          <StatusBadge status="renovado" />
          <StatusBadge status="rascunho" />
          <StatusBadge status="emitida" />
          <StatusBadge status="em_execucao" label="Ativo" />
          <StatusBadge status="concluida" label="Concluída" />
          <StatusBadge status="pago" />
        </div>
      </ComponentShowcase>
      <AmostrasDeValores />

      <h3 id="sistema-detalhe" className="pt-2 text-lg font-semibold">
        Cabeçalho, detalhe e linha do tempo
      </h3>
      {/* DetailHeader */}
      <ComponentShowcase
        id="detail-header"
        title="DetailHeader"
        description="Cabeçalho de tela de detalhe: voltar, título, subtítulo, selo de situação e ações à direita."
        code={`<DetailHeader
  backTo="/credenciados"
  backLabel="Credenciados"
  title="Pessoa Exemplo Um"
  subtitle="DRE Campo Limpo"
  status={{ variant: "ativo", label: "Ativo" }}
  actions={
    <Button variant="outline">Editar</Button>
  }
/>`}
        propsTable={[
          ['backTo', 'string', '-', 'Caminho do botão voltar'],
          ['backLabel', 'string', '-', 'Rótulo do botão voltar'],
          ['title', 'string', '-', 'Título principal (h1)'],
          ['subtitle', 'string', '-', 'Subtítulo abaixo do título'],
          ['status', '{ variant, label }', '-', 'Selo de situação'],
          ['actions', 'ReactNode', '-', 'Botões de ação (lado direito)'],
        ]}
      >
        <div className="w-full p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" className="rounded-xl">
                ← Credenciados
              </Button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight text-foreground">
                    Pessoa Exemplo Um
                  </h1>
                  <StatusBadge status="ativo" />
                </div>
                <p className="text-sm text-muted-foreground">
                  CPF {CPF_MASCARADO} · DRE Campo Limpo
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                Editar
              </Button>
              <Button variant="destructive" size="sm">
                Bloquear
              </Button>
            </div>
          </div>
        </div>
      </ComponentShowcase>
      <AmostrasDeDetalhe />

      <h3 id="sistema-indicadores" className="pt-2 text-lg font-semibold">
        Indicadores, confirmação e estados
      </h3>
      {/* StatCard */}
      <ComponentShowcase
        id="stat-card"
        title="StatCard"
        description="Cartão de indicador para painéis — ícone laranja, valor em fonte mono, tendência opcional"
        code={`<StatCard
  title="Credenciados Ativos"
  value={127}
  icon={Users}
  description="Total na DRE Campo Limpo"
  trend={{ value: 12, label: "vs mês anterior" }}
/>`}
        propsTable={[
          ['title', 'string', '-', 'Rótulo em caixa alta'],
          ['value', 'string | number', '-', 'Valor principal (mono bold)'],
          ['icon', 'LucideIcon', '-', 'Ícone com fundo laranja'],
          ['description', 'string', '-', 'Texto auxiliar'],
          ['trend', '{ value, label }', '-', 'Tendência (↑/↓)'],
        ]}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          <StatCard
            title="Credenciados"
            value={127}
            icon={Users}
            description="Ativos no sistema"
            trend={{ value: 12, label: 'vs mês' }}
          />
          <StatCard title="Veículos" value={342} icon={Car} description="Frota ativa" />
          <StatCard
            title="Termos"
            value={89}
            icon={FileText}
            description="Vigentes"
            trend={{ value: -3, label: 'vs mês' }}
          />
          <StatCard
            title="Faturamento"
            value="R$ 1,2M"
            icon={DollarSign}
            description="Mês corrente"
          />
        </div>
      </ComponentShowcase>
      {/* ConfirmDialog */}
      <ComponentShowcase
        id="confirm-dialog"
        title="ConfirmDialog"
        description="Confirmação de ação crítica (exclusão, bloqueio, cancelamento). A variante destructive pinta o botão de confirmar com a cor destrutiva."
        code={`<ConfirmDialog
  trigger={<Button variant="destructive"><Trash2 /> Excluir</Button>}
  title="Excluir credenciado?"
  description="Esta ação não pode ser desfeita."
  confirmLabel="Excluir"
  variant="destructive"
  onConfirm={() => deleteMutation.mutate()}
/>`}
        propsTable={[
          ['trigger', 'ReactNode', '-', 'Elemento que abre a confirmação'],
          ['title', 'string', '-', 'Título da confirmação'],
          ['description', 'string', '-', 'Texto descritivo'],
          ['confirmLabel', 'string', '"Confirmar"', 'Rótulo do botão de confirmar'],
          ['cancelLabel', 'string', '"Cancelar"', 'Rótulo do botão cancelar'],
          ['variant', '"default" | "destructive"', '"default"', 'Estilo do botão de confirmar'],
          ['onConfirm', '() => void', '-', 'Callback ao confirmar'],
          ['disabled', 'boolean', 'false', 'Desabilita o botão de confirmar'],
        ]}
      >
        <div className="flex gap-3">
          <ConfirmDialog
            trigger={
              <Button variant="destructive" size="sm">
                <Trash2 className="h-4 w-4 mr-1" /> Excluir
              </Button>
            }
            title="Excluir credenciado?"
            description="O credenciado Pessoa Exemplo Um será removido permanentemente. Esta ação não pode ser desfeita."
            confirmLabel="Excluir"
            variant="destructive"
            onConfirm={() => {}}
          />
          <ConfirmDialog
            trigger={
              <Button variant="outline" size="sm">
                <AlertTriangle className="h-4 w-4 mr-1" /> Bloquear
              </Button>
            }
            title="Bloquear credenciado?"
            description="O credenciado ficará impedido de operar até desbloqueio manual."
            confirmLabel="Bloquear"
            onConfirm={() => {}}
          />
        </div>
      </ComponentShowcase>
      {/* Spinner */}
      <ComponentShowcase
        id="spinner"
        title="Spinner"
        description="Indicador de carregamento em 3 tamanhos, na cor primária"
        code={`<Spinner size="sm" />
<Spinner size="md" />
<Spinner size="lg" />
<Spinner size="lg" fullPage />`}
        propsTable={[
          ['size', '"sm" | "md" | "lg"', '"md"', 'Tamanho do indicador'],
          ['fullPage', 'boolean', 'false', 'Centraliza verticalmente (min-h-screen)'],
        ]}
      >
        <div className="flex items-end gap-6">
          <div className="text-center">
            <Spinner size="sm" />
            <p className="text-[10px] text-muted-foreground mt-1">sm</p>
          </div>
          <div className="text-center">
            <Spinner size="md" />
            <p className="text-[10px] text-muted-foreground mt-1">md</p>
          </div>
          <div className="text-center">
            <Spinner size="lg" />
            <p className="text-[10px] text-muted-foreground mt-1">lg</p>
          </div>
        </div>
      </ComponentShowcase>
      {/* EmptyState */}
      <ComponentShowcase
        id="empty-state"
        title="EmptyState"
        description="Exibido quando uma listagem não tem dados — sempre com um caminho de saída"
        code={`<EmptyState
  icon={Search}
  title="Nenhum credenciado encontrado"
  description="Tente ajustar os filtros ou cadastre um novo credenciado."
  actionLabel="Novo Credenciado"
  onAction={() => navigate('/credenciados/novo')}
/>`}
        propsTable={[
          ['icon', 'LucideIcon', 'Inbox', 'Ícone exibido no topo'],
          ['title', 'string', '-', 'Título do estado vazio'],
          ['description', 'string', '-', 'Texto auxiliar com orientação'],
          ['actionLabel', 'string', '-', 'Rótulo do botão de ação'],
          ['onAction', '() => void', '-', 'Chamado pelo botão de ação'],
        ]}
      >
        <div className="w-full">
          <EmptyState
            icon={Search}
            title="Nenhum credenciado encontrado"
            description="Tente ajustar os filtros de busca ou cadastre um novo credenciado no sistema."
            actionLabel="Novo Credenciado"
            onAction={() => {}}
          />
        </div>
      </ComponentShowcase>
      {/* Sem acesso: o ErrorState real que a RotaProtegida mostra */}
      <ComponentShowcase
        id="sem-acesso"
        title="Sem acesso (ErrorState forbidden)"
        description="O que a pessoa vê quando o perfil dela não alcança uma tela ou área. Não há componente próprio de acesso negado: a RotaProtegida (@/nucleo/auth/RotaProtegida) decide e mostra o ErrorState na variante forbidden. A interface é só a primeira das três camadas: a API e o banco recusam de novo."
        code={`import { RotaProtegida } from '@/nucleo/auth/RotaProtegida';

// Área inteira: papel errado vê a recusa em tela cheia, com "Ir para a minha área"
<RotaProtegida papeis={['GESTOR', 'OPERADOR']}>
  <CascaDeArea area={AREA_OPERACAO} />
</RotaProtegida>

// Uma tela dentro de uma área já liberada: a recusa fica dentro da casca
<RotaProtegida papeis={['GESTOR_SME']} voltarPara="/sme">
  <RevisoesSmeListPage />
</RotaProtegida>

// O que ela mostra nesse segundo caso
<ErrorState
  variant="forbidden"
  description="Seu perfil não alcança esta tela. Se você precisa deste acesso, fale com o administrador do TEG."
  primaryAction={{ label: 'Voltar ao início', to: voltarPara }}
/>`}
        propsTable={[
          ['papeis', 'readonly Papel[]', '-', 'Quem entra; sem a lista, basta estar autenticado'],
          [
            'voltarPara',
            'string',
            '-',
            'Guarda de uma tela dentro da área: a recusa aparece na casca, com volta para cá',
          ],
          ['children', 'ReactNode', '-', 'O que aparece quando o papel é aceito'],
        ]}
      >
        <div className="w-full">
          <ErrorState
            variant="forbidden"
            description="Seu perfil não alcança esta tela. Se você precisa deste acesso, fale com o administrador do TEG."
            primaryAction={{ label: 'Voltar ao início', to: '/admin/design-system' }}
          />
        </div>
      </ComponentShowcase>
      {/* TegLogo */}
      <ComponentShowcase
        id="teg-logo"
        title="TegLogo"
        description="Logotipo do sistema — usado no topo da barra lateral"
        code={`<TegLogo size="sm" />
<TegLogo size="md" />`}
        propsTable={[['size', '"sm" | "md"', '"md"', 'Tamanho do contêiner']]}
      >
        <div className="flex items-center gap-4">
          <TegLogo size="sm" />
          <TegLogo size="md" />
        </div>
      </ComponentShowcase>
    </div>
  );
}
